import React, { useState, useRef, useEffect } from 'react';

/* ─── Types ──────────────────────────────────────────── */
export interface Preset { label: string; days: number; }

interface Props {
  dateFrom: string;          // YYYY-MM-DD or ''
  dateTo:   string;          // YYYY-MM-DD or ''
  days:     number;          // currently active preset (0 = All)
  today:    string;          // YYYY-MM-DD
  presets:  Preset[];
  onPreset: (days: number) => void;
  onRange:  (from: string, to: string, apiDays: number) => void;
}

/* ─── Helpers ────────────────────────────────────────── */
const MONTHS = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
];
const DOWS = ['Mo','Tu','We','Th','Fr','Sa','Su'];

/** Returns array of YYYY-MM-DD strings (or null for empty cells) for a month grid */
function buildMonthGrid(year: number, month: number): (string | null)[] {
  const firstDow = new Date(year, month, 1).getDay(); // 0=Sun
  const offset   = (firstDow + 6) % 7;               // Mon-based offset
  const days     = new Date(year, month + 1, 0).getDate();
  const cells: (string | null)[] = Array(offset).fill(null);
  for (let d = 1; d <= days; d++) {
    cells.push(
      `${year}-${String(month + 1).padStart(2,'0')}-${String(d).padStart(2,'0')}`
    );
  }
  while (cells.length % 7 !== 0) cells.push(null); // complete last row
  return cells;
}

function fmtDisplay(ymd: string): string {
  if (!ymd) return '';
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric',
  });
}

function computeApiDays(fromYmd: string): number {
  const from = new Date(fromYmd + 'T00:00:00').getTime();
  return Math.max(1, Math.ceil((Date.now() - from) / 86_400_000));
}

/* ─── Component ──────────────────────────────────────── */
export function DateRangePicker({ dateFrom, dateTo, days, today, presets, onPreset, onRange }: Props) {
  const [open,     setOpen]     = useState(false);
  const [step,     setStep]     = useState<0 | 1>(0);  // 0=pick start, 1=pick end
  const [tempFrom, setTempFrom] = useState('');
  const [hover,    setHover]    = useState('');

  const now = new Date();
  const [viewYear,  setViewYear]  = useState(now.getFullYear());
  const [viewMonth, setViewMonth] = useState(now.getMonth());

  const containerRef = useRef<HTMLDivElement>(null);

  /* close on outside click */
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false); resetStep();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  /* close on Escape */
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { setOpen(false); resetStep(); }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open]);

  function resetStep() { setStep(0); setTempFrom(''); setHover(''); }

  function openPicker() {
    setOpen(true);
    resetStep();
    // Navigate calendar to active "from" month
    if (dateFrom) {
      const [y, m] = dateFrom.split('-').map(Number);
      setViewYear(y); setViewMonth(m - 1);
    } else {
      setViewYear(now.getFullYear()); setViewMonth(now.getMonth());
    }
  }

  function prevMonth() {
    if (viewMonth === 0) { setViewYear(y => y - 1); setViewMonth(11); }
    else setViewMonth(m => m - 1);
  }
  function nextMonth() {
    if (viewMonth === 11) { setViewYear(y => y + 1); setViewMonth(0); }
    else setViewMonth(m => m + 1);
  }

  function handleDayClick(ymd: string) {
    if (ymd > today) return;
    if (step === 0) {
      setTempFrom(ymd);
      setStep(1);
    } else {
      let from = tempFrom, to = ymd;
      if (from > to) [from, to] = [to, from]; // auto-swap if user picked backwards
      onRange(from, to, computeApiDays(from));
      setOpen(false); resetStep();
    }
  }

  /* Visual range while interacting */
  function getActiveRange(): { from: string; to: string } {
    if (step === 1 && tempFrom) {
      if (hover) {
        return tempFrom <= hover
          ? { from: tempFrom, to: hover }
          : { from: hover,    to: tempFrom };
      }
      return { from: tempFrom, to: '' };
    }
    return { from: dateFrom, to: dateTo };
  }

  /* ── Calendar renderer ── */
  function renderCalendar(year: number, month: number, showPrev: boolean, showNext: boolean) {
    const cells = buildMonthGrid(year, month);
    const { from: rFrom, to: rTo } = getActiveRange();

    return (
      <div className="drp__calendar">
        <div className="drp__cal-nav">
          {showPrev
            ? <button type="button" className="drp__nav-btn" onClick={prevMonth} aria-label="Previous month">‹</button>
            : <span className="drp__nav-spacer" />
          }
          <span className="drp__cal-month">{MONTHS[month]} {year}</span>
          {showNext
            ? <button type="button" className="drp__nav-btn" onClick={nextMonth} aria-label="Next month">›</button>
            : <span className="drp__nav-spacer" />
          }
        </div>

        <div className="drp__cal-grid">
          {DOWS.map(d => <div key={d} className="drp__cal-dow">{d}</div>)}
          {cells.map((ymd, i) => {
            if (!ymd) return <div key={`e${i}`} className="drp__cal-empty" />;

            const isFuture   = ymd > today;
            const isToday    = ymd === today;
            const isStart    = ymd === rFrom;
            const isEnd      = !!rTo && ymd === rTo;
            const isInRange  = !!rFrom && !!rTo && ymd > rFrom && ymd < rTo;
            // While picking: tempFrom is highlighted as "pending start"
            const isPending  = step === 1 && ymd === tempFrom && !hover;

            const cls = [
              'drp__day',
              isFuture  ? 'drp__day--disabled' : '',
              isToday   ? 'drp__day--today'    : '',
              (isStart || isPending) ? 'drp__day--start'    : '',
              isEnd     ? 'drp__day--end'      : '',
              isInRange ? 'drp__day--in-range' : '',
            ].filter(Boolean).join(' ');

            const dayNum = parseInt(ymd.split('-')[2], 10);

            return (
              <button
                key={ymd}
                type="button"
                className={cls}
                disabled={isFuture}
                onClick={() => handleDayClick(ymd)}
                onMouseEnter={() => step === 1 && setHover(ymd)}
                onMouseLeave={() => step === 1 && setHover('')}
                aria-label={ymd}
              >
                <span className="drp__day-inner">{dayNum}</span>
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  /* ── Trigger label ── */
  const isCustom       = !!dateFrom;
  const activePreset   = presets.find(p => !isCustom && p.days === days);
  const triggerLabel   = isCustom
    ? `${fmtDisplay(dateFrom)} – ${dateTo ? fmtDisplay(dateTo) : 'Today'}`
    : (activePreset?.label ?? 'Date range');

  /* Right calendar month */
  const rightMonth = viewMonth === 11 ? 0           : viewMonth + 1;
  const rightYear  = viewMonth === 11 ? viewYear + 1 : viewYear;

  return (
    <div className="drp" ref={containerRef}>
      {/* ── Trigger ── */}
      <button
        type="button"
        className={`drp__trigger ${isCustom ? 'drp__trigger--custom' : ''}`}
        onClick={openPicker}
        aria-haspopup="true"
        aria-expanded={open}
      >
        <svg className="drp__trigger-icon" width="13" height="13" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
          <path d="M3.5 0a.5.5 0 0 1 .5.5V1h8V.5a.5.5 0 0 1 1 0V1h1a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V3a2 2 0 0 1 2-2h1V.5a.5.5 0 0 1 .5-.5zM1 4v10a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V4H1z"/>
        </svg>
        <span className="drp__trigger-label">{triggerLabel}</span>
        <svg className="drp__trigger-arrow" width="8" height="8" viewBox="0 0 10 6" fill="currentColor" aria-hidden="true">
          <path d="M0 0l5 6 5-6z"/>
        </svg>
      </button>

      {/* ── Clear custom range ── */}
      {isCustom && (
        <button
          type="button"
          className="drp__clear-btn"
          title="Clear custom date range"
          onClick={e => { e.stopPropagation(); onPreset(30); }}
        >
          ✕
        </button>
      )}

      {/* ── Popover ── */}
      {open && (
        <div className="drp__popover" role="dialog" aria-label="Date range picker">

          {/* Quick presets */}
          <div className="drp__presets">
            {presets.map(p => (
              <button
                key={p.days}
                type="button"
                className={`drp__preset ${!isCustom && days === p.days ? 'drp__preset--active' : ''}`}
                onClick={() => { onPreset(p.days); setOpen(false); resetStep(); }}
              >
                {p.label}
              </button>
            ))}
          </div>

          <div className="drp__sep" />

          {/* Step hint */}
          <p className="drp__hint">
            {step === 0
              ? <><span className="drp__hint-step">1</span> Click a start date</>
              : <><span className="drp__hint-step drp__hint-step--active">2</span> Click an end date &mdash; <em>start: {fmtDisplay(tempFrom)}</em></>
            }
          </p>

          {/* Two-month calendar */}
          <div className="drp__calendars">
            {renderCalendar(viewYear, viewMonth, true, false)}
            {renderCalendar(rightYear, rightMonth, false, true)}
          </div>

          {/* Footer */}
          <div className="drp__footer">
            <button
              type="button"
              className="drp__footer-cancel"
              onClick={() => { setOpen(false); resetStep(); }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
